import type { ChatCanvas, ChatCanvasVersion, CanvasStarterTemplate } from '@kennisbank/shared';
import { apiGet, apiPost } from './client.js';

/**
 * Canvas (issue #3) — typed client wrappers for the backend canvas routes.
 *
 * Every call is feature-flag-gated server-side: when `CANVAS_ENABLED=false`
 * the endpoints return 404 and these wrappers will throw an ApiError 404.
 * The frontend reads `me.flags.canvasEnabled` to decide whether to render
 * the canvas affordance at all — these wrappers exist for the call path
 * once the flag is on.
 */

export interface CanvasResponse {
  canvas: ChatCanvas;
}

export interface VersionsResponse {
  versions: ChatCanvasVersion[];
}

export interface CanvasRestoreResponse {
  canvas: ChatCanvas;
}

export function fetchCanvas(chatId: string): Promise<CanvasResponse> {
  return apiGet<CanvasResponse>(`/api/v1/agent/chats/${chatId}/canvas`);
}

export function fetchCanvasVersions(chatId: string): Promise<VersionsResponse> {
  return apiGet<VersionsResponse>(`/api/v1/agent/chats/${chatId}/canvas/versions`);
}

export function restoreCanvasVersion(
  chatId: string,
  v: number,
  fromTurn: string,
): Promise<CanvasRestoreResponse> {
  return apiPost<CanvasRestoreResponse>(`/api/v1/agent/chats/${chatId}/canvas/restore`, {
    v,
    fromTurn,
  });
}

/**
 * Trigger a canvas download (docx or pptx). Uses fetch directly rather
 * than apiPost because we want the binary body, not a JSON parse.
 * Returns the Blob so the caller can save it via `URL.createObjectURL`.
 */
export async function exportCanvas(
  chatId: string,
  format: 'docx' | 'pptx',
): Promise<{ blob: Blob; filename: string }> {
  const res = await fetch(`/api/v1/agent/export/canvas/${format}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ chatId }),
  });
  if (!res.ok) {
    let detail = '';
    try {
      const body = (await res.json()) as { message?: string };
      detail = body.message ?? '';
    } catch {
      /* non-JSON error body */
    }
    throw new Error(`Canvas export failed (${res.status}): ${detail || res.statusText}`);
  }
  // Filename from Content-Disposition `attachment; filename="..."`.
  const disposition = res.headers.get('content-disposition') ?? '';
  const match = /filename="([^"]+)"/i.exec(disposition);
  const filename = match?.[1] ?? `canvas.${format}`;
  const blob = await res.blob();
  return { blob, filename };
}

/**
 * Helper for the chat-input integration: builds the `/ask` body extension
 * fields for a canvas turn. Returns an empty object when canvasMode is
 * 'none' (regular ask).
 */
export function buildCanvasAskExtension(
  mode: 'create' | 'update' | 'none',
  starterTemplate?: CanvasStarterTemplate,
): Record<string, unknown> {
  if (mode === 'none') return {};
  const out: Record<string, unknown> = { canvasMode: mode };
  if (mode === 'create' && starterTemplate) {
    out.starterTemplate = starterTemplate;
  }
  return out;
}
